/* eslint-disable clubmed/no-fake-component */
import type { ReactNode } from 'react';
import React, { Children, Fragment, useState, useEffect, useCallback } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import responsiveness from 'Components/utils/responsive/Responsive';
import type { MoreResultsProps } from 'Components/frameworks/more-results/MoreResults';
import { MoreResults } from 'Components/frameworks/more-results/MoreResults';

function getWidth(fraction: number = 1): string {
  return fraction < 2 || fraction > 4 ? 'grid-cols-1' : `grid-cols-${fraction}`;
}

function filterContext(_hasOneRemaining: boolean, itemsByLine: number, isSeeMoreVisible: boolean) {
  return (_item: any, index: number) => {
    if (isSeeMoreVisible) {
      return index < itemsByLine;
    }

    return true;
  };
}

type Props = {
  children?: ReactNode;
  childrenClassName?: string;
  className?: string;
  display?: ScreenSizesProps;
  getChildren?: () => unknown;
  grid?: ScreenSizesProps;
  hasSeeMore?: boolean;
  isLoading?: boolean;
  isSynchronousGrid?: boolean;
  responsive?: Partial<ResponsiveProps>;
  seeMore?: MoreResultsProps;
  totalNumberToDisplay?: number;
};

export function Grid({
  children,
  childrenClassName,
  className,
  display = {
    mobile: 1,
    phablet: 2,
    tablet: 3,
    desktop: 3,
  },
  getChildren = () => Promise.resolve({}),
  grid = {
    mobile: 1,
    phablet: 2,
    tablet: 3,
    desktop: 3,
  },
  hasSeeMore,
  isLoading,
  isSynchronousGrid = false,
  responsive = {},
  seeMore,
  totalNumberToDisplay,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(!hasSeeMore);

  const getGrid = () => {
    const { desktop, tablet, phablet, mobile } = grid;
    return `${getWidth(mobile)} sm:${getWidth(phablet)} md:${getWidth(tablet)} lg:${getWidth(desktop)}`;
  };

  const getItemsByLine = useCallback((): number => {
    const { desktop = 1, tablet = 1, phablet = 1, mobile = 1 } = display;
    const { isBrowserGreaterThan = {} as Record<string, boolean> } = responsive;

    if (isBrowserGreaterThan.M && desktop > -1) {
      return desktop;
    }

    if (isBrowserGreaterThan.S && tablet > -1) {
      return tablet;
    }

    if (isBrowserGreaterThan.XS && phablet > -1) {
      return phablet;
    }

    return mobile;
  }, [display, responsive]);

  const getNumberOfItemsByLineExcludingSeeMore = useCallback((): number => {
    return getItemsByLine();
  }, [getItemsByLine]);

  const getRest = useCallback(() => {
    return totalNumberToDisplay
      ? totalNumberToDisplay - getItemsByLine()
      : Children.count(children) - getItemsByLine();
  }, [totalNumberToDisplay, children, getItemsByLine]);

  const onClickSeeMore = useCallback(() => {
    if (isSynchronousGrid || totalNumberToDisplay === getItemsByLine()) {
      setIsExpanded(true);
    }

    if ((totalNumberToDisplay ?? 0) > getItemsByLine()) {
      getChildren();
    }
  }, [isSynchronousGrid, totalNumberToDisplay, getItemsByLine, getChildren]);

  const prevIsLoadingRef = React.useRef(isLoading);
  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading && !isExpanded) {
      setIsExpanded(true);
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading, isExpanded]);

  const getSeeMore = () => {
    return hasSeeMore && getRest() > 0 && !isExpanded ? (
      <MoreResults
        {...seeMore}
        className="mt-20"
        isLoading={isLoading}
        number={getRest()}
        onClick={onClickSeeMore}
      />
    ) : null;
  };

  const getGridChildren = (): Array<any> => {
    const hasOneRemaining = getRest() === 1;
    const filterItem = filterContext(
      hasOneRemaining,
      getNumberOfItemsByLineExcludingSeeMore(),
      !isExpanded,
    );
    return Children.toArray(children)
      .filter(filterItem)
      .map((item) => {
        const key = React.isValidElement(item) ? item.key : undefined;
        return (
          <div
            className={classnames('animation-duration-500 animate-fadeIn', childrenClassName)}
            data-testid="Grid-item"
            key={key}
          >
            {item}
          </div>
        );
      });
  };

  const getItems = (): ReactNode[] => {
    return [...getGridChildren()].filter((i) => i);
  };

  const items = getItems();
  return items.length ? (
    <Fragment>
      <div className={classnames('relative grid gap-20', getGrid(), className)} data-testid="grid">
        {items.map((item, index) => (
          /* eslint-disable react/no-array-index-key */
          <Fragment key={index}>{item}</Fragment>
        ))}
      </div>
      {getSeeMore()}
    </Fragment>
  ) : null;
}
export default responsiveness(Grid);
